import { dashboardApi } from "#api";
import { FC, useEffect, useRef } from "react";
import { Pagination } from "#dashboard/components/pagination.component";
import { SelectPerPageDropdown } from "#dashboard/components/selectPerPageDropdown.component";
import { useAppContext, useSafeState } from "#hooks";
import { AllowListSearchBar } from "./allowListSearchBar.component";
import { AllowListFilter } from "#types";

type AllowListEntry = { email: string };
type AllowListPageResponse = { total: number; allowedUsers: AllowListEntry[] };

export const AllowListTable: FC = () => {

    const { setApiStatus, currentEnvironment } = useAppContext();
    const [isLoading, setIsLoading] = useSafeState<boolean>(true);
    const [entries, setEntries] = useSafeState<AllowListEntry[]>([]);
    const [pageSize, setPageSize] = useSafeState<number>(10);
    const [currentPage, setCurrentPage] = useSafeState<number>(1);
    const totalPagesRef = useRef<number>(1);
    const [newEmail, setNewEmail] = useSafeState<string>("");
    const [filter, setFilter] = useSafeState<AllowListFilter>({});

    const buildMongoFilter = (uiFilter: AllowListFilter): Record<string, unknown> | undefined => {
        const email = uiFilter.email?.trim();
        if (!email) return undefined;

        return { email: { $regex: email, $options: "i" } };
    };

    const fetchAllowedUsers = async () => {
        setIsLoading(true);
        try {
            const mongoFilter = buildMongoFilter(filter);
            const res = await dashboardApi.getAllAllowedUsers(mongoFilter, currentPage, pageSize);
            const data = (res.data ?? { total: 0, allowedUsers: [] }) as AllowListPageResponse;
            totalPagesRef.current = Math.max(1, Math.ceil((data.total ?? 0) / pageSize));
            setEntries(data.allowedUsers ?? []);
            const safePage = Math.min(currentPage, totalPagesRef.current);
            if (safePage !== currentPage) setCurrentPage(safePage);
        } catch (error: any) {
            setApiStatus({
                code: error?.response?.data?.statusCode,
                message: error?.response?.data?.message,
            });
            setEntries([]);
            totalPagesRef.current = 1;
        } finally {
            setIsLoading(false);
        }
    };

    const createAllowedUser = async () => {
        const email = newEmail.trim().toLowerCase();
        if (!email) return;

        try {
            await dashboardApi.createAllowedUser(email);
            setNewEmail("");
            setCurrentPage(1);
            await fetchAllowedUsers();
        } catch (error: any) {
            setApiStatus({
                code: error?.response?.data?.statusCode,
                message: error?.response?.data?.message,
            });
        }
    };

    const deleteAllowedUser = async (email: string) => {
        const confirmed = window.confirm(`Remove ${email} from the allow list?`);
        if (!confirmed) return;
        try {
            await dashboardApi.deleteAllowedUser(email);
            if (entries.length === 1 && currentPage > 1) {
                setCurrentPage(currentPage - 1);
                return;
            }
            await fetchAllowedUsers();
        } catch (error: any) {
            setApiStatus({
                code: error?.response?.data?.statusCode,
                message: error?.response?.data?.message,
            });
        }
    };

    useEffect(() => {
        fetchAllowedUsers();
    }, [currentEnvironment, pageSize, currentPage, filter]);

    if (isLoading) return <div className="lds-hourglass" />;

    return (
        <div className="pd-m main-dashboard-container mt-s">
            <div className="three-columns-expand-two">
                <div className="two-columns-expand-two">
                    <label htmlFor="allowlist-email" className="vertical-align mr-m bold search-bar">
                        Add an email:
                    </label>
                    <input
                        type="email"
                        id="search-query"
                        className="pl-m vertical-align"
                        value={newEmail}
                        onChange={(event) => setNewEmail(event.target.value)}
                        onKeyDown={(event) => {
                            if (event.key === "Enter") void createAllowedUser();
                        }}
                    />
                </div>

                <div className="vertical-align">
                    <button
                        type="button"
                        className="searchBy-btn bold white-text"
                        disabled={!newEmail.trim()}
                        onClick={() => void createAllowedUser()}
                    >
                        ADD
                    </button>
                </div>
                <div></div>
            </div>

            <AllowListSearchBar setFilter={setFilter} setCurrentPage={setCurrentPage} />

            <table className="center-wrapper table mt-s">
                <tbody>
                    {entries.map((entry, index) => (
                        <tr key={`${entry.email}_${String(index)}`} className="table-row">
                            <td className="table-cell">{entry.email}</td>
                            <td className="table-cell">
                                <div className='three-rows-expand-one-three ml-m'>
                                    <div />
                                    <button className="center-wrapper bold del-allow-list-button mb-s" onClick={() => deleteAllowedUser(entry.email)}>DELETE</button>
                                    <div />
                                </div>
                            </td>
                        </tr>
                    ))}

                    {!entries.length && (
                        <tr className="table-row">
                            <td className="table-cell" colSpan={2}>
                                No allow list entries found.
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
            <Pagination totalPages={totalPagesRef.current} currentPage={currentPage} setCurrentPage={setCurrentPage} />
        </div>
    );
};